import { supabase } from '../lib/supabase';
import type { Driver } from '../lib/types';

export type DriverInsert = {
  full_name: string;
  email?: string | null;
  phone?: string | null;
  driver_code?: string | null;
  license_no?: string | null;
  vehicle_id?: string | null;
  company_id?: string | null;
  status?: Driver['status'];
  performance_score?: number;
};

export type DriverUpdate = Partial<DriverInsert>;

function normalizeDriverCode(code: string): string {
  return code.trim().replace(/\s+/g, '');
}

function friendlyDriverError(message: string): string {
  if (message.includes('drivers_driver_code_uidx') || message.includes('already registered to')) {
    return message.includes('already registered to')
      ? message
      : 'This förarkod is already used by another driver. Each taxilegitimation number must be unique.';
  }
  return message;
}

export function useDriverMutations() {
  async function createDriver(data: DriverInsert) {
    const { data: result, error } = await supabase
      .from('drivers')
      .insert(data)
      .select('*, vehicle:vehicles(*)')
      .single();
    if (error) return { data: null, error: friendlyDriverError(error.message) };
    return { data: result as Driver, error: null };
  }

  async function updateDriver(id: string, data: DriverUpdate) {
    const payload = { ...data };
    if (payload.driver_code != null) {
      payload.driver_code = normalizeDriverCode(payload.driver_code);
    }

    const { data: rpcData, error: rpcErr } = await supabase.rpc('update_driver_profile_dashboard', {
      p_driver_id: id,
      p_full_name: payload.full_name ?? null,
      p_email: payload.email ?? null,
      p_phone: payload.phone ?? null,
      p_driver_code: payload.driver_code ?? null,
      p_license_no: payload.license_no ?? null,
      p_vehicle_id: payload.vehicle_id ?? null,
    });

    if (!rpcErr && rpcData) {
      const { data: result, error } = await supabase
        .from('drivers')
        .select('*, vehicle:vehicles(*)')
        .eq('id', id)
        .single();
      if (error) return { data: null, error: friendlyDriverError(error.message) };
      return { data: result as Driver, error: null };
    }

    // Fallback if migration 024 not applied yet
    if (rpcErr?.message?.includes('Could not find') || rpcErr?.code === 'PGRST202') {
      const { data: result, error } = await supabase
        .from('drivers')
        .update(payload)
        .eq('id', id)
        .select('*, vehicle:vehicles(*)')
        .single();
      if (error) return { data: null, error: friendlyDriverError(error.message) };
      return { data: result as Driver, error: null };
    }

    return { data: null, error: friendlyDriverError(rpcErr?.message ?? 'Update failed') };
  }

  async function deleteDriver(id: string) {
    const { error } = await supabase.from('drivers').delete().eq('id', id);
    return { error: error?.message ?? null };
  }

  return { createDriver, updateDriver, deleteDriver };
}
